import { useState, useEffect, useMemo } from "react";
import { gql } from "urql";
import {
  useGetCustomVariableQuery,
  useMeQuery,
} from "src/gql/generated/graphql";
import { PATH, TOKEN_KEY, COMPANY_VARIABLE_KEY, ARG_STATUS } from "src/const";
import { useHistory } from "react-router-dom";
import { cookieKeyToken } from "src/util/token";
import { Cookies } from "react-cookie";
import { isBlank } from "src/util/textUtil";

gql`
  query GetCustomVariable($variable: String!, $owner: ID!) {
    customVariable(variable: $variable, owner: $owner) {
      value
    }
  }
`;

export const MeQuery = gql`
  fragment MeRoomField on Room {
    id
    datastoreId
    name
  }

  fragment MeField on User {
    id
    datastoreId
    name
    profile {
      presignedIcon
    }
    roomGroup {
      rooms {
        ...MeRoomField
      }
    }
    company {
      id
      datastoreId
      name
    }
  }

  query me {
    me {
      ...MeField
    }
  }
`;

const ME_CACHE = 'safie_me_cache';

export const useSafieApiKey = (assignApiKey: string | null = null) => {
  // キャッシュから初期値を取得
  const cachedMe = sessionStorage.getItem(ME_CACHE);
  const [me, setMe] = useState<any>(cachedMe ? JSON.parse(cachedMe) : undefined);
  const [result] = useMeQuery();
  const history = useHistory();

  useEffect(() => {
    const cookie = new Cookies();
    let token = cookie.get(TOKEN_KEY);

    // NOTE: RURA Web の接客画面から ifarame で呼び出される場合は、RURA Web 側のトークンを優先する。
    if (window.parent != null) {
      const ruraToken = cookie.get<string>(cookieKeyToken);
      if (ruraToken && token !== ruraToken) {
        cookie.set(TOKEN_KEY, ruraToken, {
          maxAge: 10 * 24 * 60 * 60,
          sameSite: "none",
          secure: true,
        });
        window.location.reload();
        return;
      }
    }

    if (!token) {
      const colaboToken = cookie.get(cookieKeyToken);
      if (colaboToken) {
        cookie.set(TOKEN_KEY, colaboToken ?? "", {
          maxAge: 10 * 24 * 60 * 60,
          sameSite: "none",
          secure: true,
        });
        token = colaboToken;
        window.location.reload();
      }
    }

    if (!token) {
      history.push(PATH.LOGIN);
    }
  }, [history]);

  useEffect(() => {
    if (result.data?.me === undefined) {
      return;
    }
    setMe(result.data.me);
    // meをキャッシュ
    sessionStorage.setItem(ME_CACHE, JSON.stringify(result.data.me));
  }, [result.data]);

  // 手動指定のAPIキーがある場合はGraphQLクエリをpauseする
  // me stateとresult.dataの両方をチェック（state更新前でもresult.dataがあれば実行）
  const companyId = me?.company?.id || result.data?.me?.company?.id;
  const shouldPauseQuery = isBlank(companyId) || !!assignApiKey;

  const [{ data, fetching: customVarFetching }] = useGetCustomVariableQuery({
    variables: { variable: COMPANY_VARIABLE_KEY, owner: companyId },
    pause: shouldPauseQuery,
    requestPolicy: "cache-and-network",
  });

  const safieApiKey = useMemo(() => {
    // 手動指定のAPIキーが最優先
    if (assignApiKey) {
      return { status: ARG_STATUS.ASSIGN, data: assignApiKey };
    }

    // GraphQLからの取得
    if (!data) {
      return { status: ARG_STATUS.INIT, data: undefined };
    }

    const api_key = data.customVariable.value ?? undefined;
    if (!api_key) {
      return { status: ARG_STATUS.ERROR, data: undefined };
    }

    return { status: ARG_STATUS.SUCCESS, data: api_key };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, assignApiKey]);

  return { safieApiKey };
};
